import type { CSSObject } from "@mui/material/styles";

export const visuallyHidden = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: -1,
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
} as const;

/**
 * Usage:
 * ...hideScrollX,
 * ...hideScrollY,
 */
export const hideScrollX: CSSObject = {
  "&::-webkit-scrollbar": { display: "none" },
  msOverflowStyle: "none",
  overflowX: "auto",
  scrollbarWidth: "none",
};

export const hideScrollY: CSSObject = {
  "&::-webkit-scrollbar": { display: "none" },
  msOverflowStyle: "none",
  overflowY: "auto",
  scrollbarWidth: "none",
};

/**
 * Usage:
 * ...bgBlur({ color: `varAlpha(theme.vars.palette.background.paperChannel, 0.8)`, imgUrl: '/assets/background/overlay.png', blur: 6 }),
 */
export type BgBlurProps = {
  blur?: number;
  color: string;
  imgUrl?: string;
};

/**
 * Usage:
 * ...bgGradient({ color: `to right, ${theme.vars.palette.grey[900]} 25%, ${varAlpha(theme.vars.palette.primary.darkerChannel, 0.88)}`, imgUrl: '/assets/background/overlay.png' }),
 */
export type BgGradientProps = {
  color: string;
  imgUrl?: string;
};

export function bgBlur({ blur = 6, color, imgUrl }: BgBlurProps): CSSObject {
  if (imgUrl) {
    return {
      "&::before": {
        backdropFilter: `blur(${blur}px)`,
        backgroundColor: color,
        content: '""',
        height: "100%",
        left: 0,
        position: "absolute",
        top: 0,
        WebkitBackdropFilter: `blur(${blur}px)`,
        width: "100%",
        zIndex: 9,
      },
      backgroundImage: `url(${imgUrl})`,
      position: "relative",
    };
  }
  return {
    backdropFilter: `blur(${blur}px)`,
    backgroundColor: color,
    WebkitBackdropFilter: `blur(${blur}px)`,
  };
}

export function bgGradient({ color, imgUrl }: BgGradientProps): CSSObject {
  if (imgUrl) {
    return {
      background: `linear-gradient(${color}), url(${imgUrl})`,
      backgroundPosition: "center center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
    };
  }
  return { background: `linear-gradient(${color})` };
}

/**
 * Usage:
 * ...textGradient(`to right, ${theme.vars.palette.text.primary}, ${alpha(theme.vars.palette.text.primary, 0.2)}`
 */
export function textGradient(color: string): CSSObject {
  return {
    background: `linear-gradient(${color})`,
    backgroundClip: "text",
    color: "transparent",
    textFillColor: "transparent",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };
}
